import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  UserActivityEvent,
  userActivityEventFromJSON,
  UserActivityEventType,
} from '@repo/sdk/models/components';

import { useStream } from '@/hooks/use-stream';
import { useUser } from '@/hooks/use-user';
import type { AgentPresence } from '@/queries/gaia-api/user-activity-query';
import { userActivityQuery } from '@/queries/gaia-api/user-activity-query';
import { STREAM_TYPES } from '@/state/stream-status.state';

export const useUserActivityStream = () => {
  const { userId } = useUser();
  const queryClient = useQueryClient();
  const { queryKey, webSocketUrl, enabled } = userActivityQuery({ userId });

  const handleUserActivityEvent = useCallback(
    (event: UserActivityEvent) => {
      switch (event.type) {
        case UserActivityEventType.Keepalive: {
          // Keep Alive is a keep alive message.
          // Maybe: A single keepalive cache for all sockets?
          console.debug(`User Activity keep alive at ${new Date().toISOString()}`);
          break;
        }
        case UserActivityEventType.UserActivity: {
          if (!event.agentPresence) {
            break;
          }
          const agentPresence: AgentPresence = {
            agentId: event.agentPresence.subject.agentId,
            status: event.agentPresence.status,
            statusAt: event.agentPresence.statusAt,
            updatedAt: new Date(),
          };
          queryClient.setQueryData(queryKey, (old: AgentPresence[]) => {
            const { foundMatch, newSet } = (old || []).reduce(
              (acc: { foundMatch: boolean; newSet: AgentPresence[] }, presence: AgentPresence) => {
                if (presence.agentId === agentPresence.agentId) {
                  return {
                    foundMatch: true,
                    newSet: [...acc.newSet, agentPresence],
                  };
                }
                return {
                  foundMatch: acc.foundMatch,
                  newSet: [...acc.newSet, presence],
                };
              },
              { foundMatch: false, newSet: [] }
            );
            return foundMatch ? newSet : [...newSet, agentPresence];
          });

          break;
        }
        default:
          break;
      }
    },
    [queryClient, queryKey]
  );

  return useStream<UserActivityEvent>(enabled ? webSocketUrl : null, {
    onReceiveContent: handleUserActivityEvent,
    parse: userActivityEventFromJSON,
    streamType: STREAM_TYPES.USER_ACTIVITY,
    displayName: 'User Activity',
  });
};
